import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Command } from 'cmdk';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/permissions/use-permissions';
import { actionRegistry } from './registry';
import { matchesPaletteShortcut, modifierLabel } from './platform';
import { getRecentActionIds, pushRecentActionId } from './recent';
import type { CommandAction, CommandGroup } from './types';
import { useBuildingsCommandHits } from './adapters/buildings';
import { usePortfoliosCommandHits } from './adapters/portfolios';
import { useEvaluationsCommandHits } from './adapters/evaluations';
import type { CommandEntityHit } from './adapters/types';

/**
 * W16-F2-S3 — programmatic open contract. Other components (e.g. the
 * topbar `<SearchTrigger>`) dispatch this CustomEvent on `window` to
 * open the palette without piggybacking the keyboard listener.
 */
export const PALETTE_OPEN_EVENT = 'senviro:command-palette:open';

export function openCommandPalette(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PALETTE_OPEN_EVENT));
}

/**
 * Cmd+K (Ctrl+K) command palette. Mounted once globally in the app shell.
 *
 * Listens for the platform-appropriate open shortcut on `window`, renders a
 * `cmdk`-based filterable list grouped by `CommandGroup`. Actions whose
 * `permissions` aren't satisfied by the current role are filtered out.
 *
 * The palette is a portal — it lives outside any layout container and
 * doesn't affect document flow when closed.
 */
export function CommandPalette(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { t } = useTranslation();
  const { can } = usePermissions();

  const allActions = useSyncExternalStore(
    actionRegistry.subscribe,
    actionRegistry.list,
    actionRegistry.list,
  );

  // Filter by RBAC. Actions without `permissions` are always visible.
  const visibleActions = useMemo<ReadonlyArray<CommandAction>>(() => {
    return allActions.filter((action) => {
      if (!action.permissions || action.permissions.length === 0) return true;
      return action.permissions.every((p) => can(p.resource, p.action));
    });
  }, [allActions, can]);

  // Compute Recent group from localStorage IDs, in invocation order.
  const recentActions = useMemo<ReadonlyArray<CommandAction>>(() => {
    const ids = getRecentActionIds();
    return ids
      .map((id) => visibleActions.find((a) => a.id === id))
      .filter((a): a is CommandAction => a !== undefined);
  }, [visibleActions]);

  // W129-F5-S2 — dynamic entity hits. Adapters read cached TanStack Query
  // data + filter client-side; empty-query short-circuits to [] so a cold
  // open doesn't dump every building. Each hit's navigation closes the
  // palette via the same `runAction`-equivalent flow below.
  const buildingHits = useBuildingsCommandHits(query);
  const portfolioHits = usePortfoliosCommandHits(query);
  const evaluationHits = useEvaluationsCommandHits(query);

  // Group actions for display (Recent > Navigation > Admin).
  const grouped = useMemo(() => {
    const groups: Record<CommandGroup, CommandAction[]> = {
      navigation: [],
      admin: [],
      recent: [...recentActions],
    };
    visibleActions.forEach((action) => {
      if (action.group === 'navigation') groups.navigation.push(action);
      else if (action.group === 'admin') groups.admin.push(action);
    });
    return groups;
  }, [visibleActions, recentActions]);

  // Global open-shortcut listener.
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      if (matchesPaletteShortcut(event)) {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, []);

  // W16-F2-S3 — programmatic open trigger so the topbar SearchTrigger
  // (and any future affordance) can open the palette without simulating
  // a keyboard event.
  useEffect(() => {
    const handler = (): void => setOpen(true);
    window.addEventListener(PALETTE_OPEN_EVENT, handler);
    return () => {
      window.removeEventListener(PALETTE_OPEN_EVENT, handler);
    };
  }, []);

  // Reset query when closed so the next open starts fresh.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const runAction = (action: CommandAction): void => {
    pushRecentActionId(action.id);
    setOpen(false);
    void action.run();
  };

  // W129-F5-S2 — entity hits don't accrete into the "Recent" list; that
  // list is meant for actions that are likely to be repeated (admin
  // tools, settings shortcuts), not entity navigations (which the user
  // typically reaches once-then-moves-on within a session).
  const runEntityHit = (hit: CommandEntityHit): void => {
    setOpen(false);
    hit.onSelect();
  };

  if (!open) {
    return (
      <span className="sr-only" aria-live="polite">
        {t('commandPalette.openHint', { mod: modifierLabel() })}
      </span>
    );
  }

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label={t('commandPalette.label')}
      className="fixed left-1/2 top-[20%] z-50 w-[min(640px,92vw)] -translate-x-1/2 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-2xl"
    >
      <div className="border-b border-border px-3">
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder={t('commandPalette.placeholder')}
          className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          autoFocus
        />
      </div>
      <Command.List className="max-h-[400px] overflow-auto p-2">
        <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
          {t('commandPalette.noResults')}
        </Command.Empty>

        {grouped.recent.length > 0 ? (
          <Command.Group
            heading={t('commandPalette.group.recent')}
            className="mb-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
          >
            {grouped.recent.map((action) => (
              <PaletteItem key={`recent-${action.id}`} action={action} onRun={runAction} />
            ))}
          </Command.Group>
        ) : null}

        {grouped.navigation.length > 0 ? (
          <Command.Group
            heading={t('commandPalette.group.navigation')}
            className="mb-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
          >
            {grouped.navigation.map((action) => (
              <PaletteItem key={action.id} action={action} onRun={runAction} />
            ))}
          </Command.Group>
        ) : null}

        {grouped.admin.length > 0 ? (
          <Command.Group
            heading={t('commandPalette.group.admin')}
            className="mb-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
          >
            {grouped.admin.map((action) => (
              <PaletteItem key={action.id} action={action} onRun={runAction} />
            ))}
          </Command.Group>
        ) : null}

        {/* W129-F5-S2 — dynamic entity-search groups. Each adapter
            returns [] when the input is empty, so these groups
            disappear on a cold-open. */}
        {buildingHits.length > 0 ? (
          <Command.Group
            heading={t('commandPalette.group.buildings', {
              defaultValue: 'Buildings',
            })}
            className="mb-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
          >
            {buildingHits.map((hit) => (
              <EntityHitItem key={hit.id} hit={hit} onRun={runEntityHit} />
            ))}
          </Command.Group>
        ) : null}

        {portfolioHits.length > 0 ? (
          <Command.Group
            heading={t('commandPalette.group.portfolios', {
              defaultValue: 'Portfolios',
            })}
            className="mb-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
          >
            {portfolioHits.map((hit) => (
              <EntityHitItem key={hit.id} hit={hit} onRun={runEntityHit} />
            ))}
          </Command.Group>
        ) : null}

        {evaluationHits.length > 0 ? (
          <Command.Group
            heading={t('commandPalette.group.evaluations', {
              defaultValue: 'Recent evaluations',
            })}
            className="mb-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
          >
            {evaluationHits.map((hit) => (
              <EntityHitItem key={hit.id} hit={hit} onRun={runEntityHit} />
            ))}
          </Command.Group>
        ) : null}
      </Command.List>
    </Command.Dialog>
  );
}

interface PaletteItemProps {
  action: CommandAction;
  onRun: (action: CommandAction) => void;
}

function PaletteItem({ action, onRun }: PaletteItemProps): JSX.Element {
  const Icon = action.icon;
  return (
    <Command.Item
      value={`${action.label} ${action.id}`}
      onSelect={() => {
        onRun(action);
      }}
      className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm aria-selected:bg-accent aria-selected:text-accent-foreground"
    >
      {Icon ? <Icon className="h-4 w-4 opacity-70" /> : null}
      <span className="flex-1">{action.label}</span>
      {action.shortcut ? (
        <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
          {action.shortcut}
        </kbd>
      ) : null}
    </Command.Item>
  );
}

interface EntityHitItemProps {
  hit: CommandEntityHit;
  onRun: (hit: CommandEntityHit) => void;
}

/**
 * Row for a dynamic entity-search result (W129-F5-S2). Same density as
 * `<PaletteItem>` so the palette body has visual continuity between
 * static actions + entity navigations. The sublabel (partner name on
 * buildings, kind on portfolios) gets a smaller muted treatment.
 */
function EntityHitItem({ hit, onRun }: EntityHitItemProps): JSX.Element {
  const Icon = hit.icon;
  return (
    <Command.Item
      value={`${hit.label} ${hit.sublabel ?? ''} ${hit.id}`}
      onSelect={() => {
        onRun(hit);
      }}
      data-testid={`palette-entity-${hit.id}`}
      className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm aria-selected:bg-accent aria-selected:text-accent-foreground"
    >
      {Icon ? <Icon className="h-4 w-4 opacity-70" /> : null}
      <span className="flex-1 truncate">{hit.label}</span>
      {hit.sublabel ? (
        <span className="truncate text-xs text-muted-foreground">
          {hit.sublabel}
        </span>
      ) : null}
    </Command.Item>
  );
}
